import { registerSW } from "virtual:pwa-register";

const SW_UPDATE_INTERVAL_MS = 15 * 60 * 1000;
const SAFE_CHECK_INTERVAL_MS = 5 * 1000;
// Right after load the user hasn't started anything yet, so updating is invisible.
const STARTUP_GRACE_MS = 10 * 1000;
// Idle long enough with no open form/dialog -> reload is unnoticeable.
const IDLE_SAFE_MS = 30 * 1000;
// Upper bound so a forgotten focused input can't block updates forever.
const IDLE_FORCE_MS = 5 * 60 * 1000;
const CHUNK_RELOAD_KEY = "chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 30 * 1000;

const pageLoadedAt = Date.now();
let lastInteractionAt = 0;
let updateScheduled = false;

function trackUserActivity() {
  const mark = () => {
    lastInteractionAt = Date.now();
  };
  ["pointerdown", "keydown", "wheel", "touchstart", "input"].forEach((evt) =>
    window.addEventListener(evt, mark, { capture: true, passive: true })
  );
}

function isUserEditing() {
  const el = document.activeElement;
  if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) {
    return true;
  }
  return Boolean(
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
    )
  );
}

function isSafeToReload() {
  if (document.visibilityState === "hidden") return true;

  const now = Date.now();
  if (!lastInteractionAt && now - pageLoadedAt < STARTUP_GRACE_MS) return true;

  const idleFor = now - Math.max(lastInteractionAt, pageLoadedAt);
  if (idleFor >= IDLE_FORCE_MS) return true;
  return idleFor >= IDLE_SAFE_MS && !isUserEditing();
}

/**
 * Activates the waiting service worker (registerSW then reloads on `controlling`)
 * at the first moment the reload can't interrupt the user.
 */
function applyUpdateWhenSafe(updateSW) {
  if (updateScheduled) return;
  updateScheduled = true;

  let timer = null;
  const tryApply = () => {
    if (!isSafeToReload()) return;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", tryApply);
    updateSW(true).catch(() => window.location.reload());
  };

  timer = setInterval(tryApply, SAFE_CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", tryApply);
  tryApply();
}

// Installed PWAs rarely navigate, so the browser alone almost never re-checks sw.js.
function watchForSWUpdates(registration) {
  const check = async () => {
    if (registration.installing || !navigator.onLine) return;
    try {
      await registration.update();
    } catch {
      // server unreachable; retry on next tick
    }
  };

  setInterval(check, SW_UPDATE_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("online", check);
}

// After a deploy the old hashed chunks are gone from the server and the SW cache,
// so a lazy route import from an old page fails. This also covers browsers without SW support.
function recoverFromStaleChunks() {
  window.addEventListener("vite:preloadError", (event) => {
    let lastReload = 0;
    try {
      lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    } catch {
      // storage unavailable (private mode); fall through and reload
    }
    if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return;

    event.preventDefault();
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    window.location.reload();
  });
}

export function setupAutoUpdate() {
  recoverFromStaleChunks();
  if (!("serviceWorker" in navigator)) return;

  trackUserActivity();

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      applyUpdateWhenSafe(updateSW);
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration) watchForSWUpdates(registration);
    },
    onOfflineReady() {},
  });
}
